/**
 * Settings page wrapper that fetches real subscription from API
 * and passes it to SubscriptionManagement (replacing hardcoded 150/500, 2.30/5 GB).
 */
import React, { useState, useEffect } from 'react';
import {
  Alert,
  CircularProgress,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from '@mui/material';
import { Lock as LockIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import SubscriptionManagement from './SubscriptionManagement';
import PreferencesSettings from '../settings/PreferencesSettings';
import ChangePasswordSection from '../settings/ChangePasswordSection';
import NotificationSettings from '../settings/NotificationSettings';
import { Subscription, User } from '../../types/dashboard';
import { apiService, ApiError } from '../../services/apiService';
import { useAuth } from '../../contexts/AuthContext';

const STORAGE_LIMIT_BY_PLAN: Record<string, number> = {
  trial: 0.1,
  starter: 1,
  pro: 5,
  elite: 10,
  ultimate: 20,
  basic: 1,
  standard: 2,
  enterprise: 20,
  desktop: 5,
};

const normalizePlan = (tier: string): Subscription['plan'] => {
  const legacy: Record<string, Subscription['plan']> = {
    basic: 'starter',
    standard: 'pro',
    enterprise: 'ultimate',
    desktop: 'pro',
  };
  const key = (tier || 'trial').toLowerCase();
  if (key in legacy) return legacy[key];
  if (['trial', 'starter', 'pro', 'elite', 'ultimate'].includes(key)) {
    return key as Subscription['plan'];
  }
  return 'trial';
};

interface SettingsPageProps {
  user: User;
  onPlanChange?: (newPlan: Subscription['plan']) => void;
  onPaymentMethodUpdate?: () => void;
  onUserInfoUpdate?: (userInfo: Partial<User>) => void | Promise<void>;
}

const SettingsPage: React.FC<SettingsPageProps> = ({
  user,
  onPlanChange = () => {},
  onPaymentMethodUpdate = () => {},
  onUserInfoUpdate = () => {},
}) => {
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { token: authToken } = useAuth();

  useEffect(() => {
    let cancelled = false;
    const t = authToken;
    const load = async () => {
      if (!t) {
        if (!cancelled) {
          setError('Not signed in');
          setLoading(false);
          setSubscription(null);
        }
        return;
      }
      try {
        setLoading(true);
        setError(null);
        const [subResponse, usageResponse] = await Promise.all([
          apiService.getSubscription(t),
          apiService.getUsageStatistics(30, t).catch(() => null),
        ]);
        if (cancelled) return;
        if (subResponse.success && subResponse.data) {
          const d = subResponse.data;
          const plan = normalizePlan(d.tier);
          const webhookUsage = usageResponse?.success && usageResponse?.data
            ? usageResponse.data.current_usage
            : 0;
          const storageLimit = STORAGE_LIMIT_BY_PLAN[plan] ?? 5;
          setSubscription({
            plan,
            status: d.status === 'trial' ? 'active' : (d.status as Subscription['status']),
            webhookLimit: d.webhook_limit,
            webhookUsage,
            storageLimit,
            storageUsage: 0, // Backend usage API doesn't provide storage yet
            expiresAt: (d.trial_end_date || d.subscription_end_date || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()).toString(),
            autoRenew: false,
          });
        } else {
          setSubscription(null);
        }
      } catch (err) {
        if (cancelled) return;
        const message = err instanceof ApiError ? err.message : 'Failed to load subscription';
        setError(message);
        setSubscription(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [authToken]);

  if (loading && !subscription) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (!subscription) {
    return (
      <Alert severity="warning" sx={{ m: 2 }}>
        {error || 'Your subscription could not be loaded.'} Please refresh, or use “Manage Billing” once your plan is active.
      </Alert>
    );
  }

  return (
    <Box>
      <SubscriptionManagement
        subscription={subscription}
        billingHistory={[]}
        user={user}
        onPlanChange={onPlanChange}
        onPaymentMethodUpdate={onPaymentMethodUpdate}
        onUserInfoUpdate={onUserInfoUpdate}
        loading={loading}
      />
      <Accordion sx={{ mt: 3 }} disableGutters>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="change-password-content" id="change-password-header">
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <LockIcon sx={{ mr: 1 }} color="primary" />
            <Box>
              <Typography variant="h6">Change password</Typography>
              <Typography variant="body2" color="text.secondary">
                Update the password you use to sign in.
              </Typography>
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <ChangePasswordSection embedded />
        </AccordionDetails>
      </Accordion>
      <NotificationSettings />
      <PreferencesSettings />
    </Box>
  );
};

export default SettingsPage;
